import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Button, Space, message, Spin, Typography, Tag, Input, Select } from 'antd';
import { SaveOutlined, UndoOutlined, SearchOutlined, ClearOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { 
  DataGrid, 
  gridClasses, 
  useGridApiRef,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import {
  Box,
  Pagination,
  PaginationItem,
  styled,
  Card as MuiCard,
  CardContent
} from '@mui/material';
import { useFetchArticlesZoneEtage, useBulkUpdateZoneEtage } from '../../hooks/articleHooks';
import { useFetchAllEntrepots } from '../../hooks/entrepotHooks';
import './ArticleZoningManager.css';

const { Title, Text } = Typography;

// Custom pagination component with black theme
function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Pagination
      color="primary"
      variant="outlined"
      shape="rounded"
      size="small"
      page={page + 1}
      count={pageCount}
      renderItem={(props) => <PaginationItem {...props} disableRipple />}
      onChange={(event, value) => apiRef.current.setPage(value - 1)}
      sx={{ 
        marginTop: '5px',
        display: 'flex',
        justifyContent: 'center',
        '& .MuiPaginationItem-root': {
          minWidth: '24px',
          height: '24px',
          fontSize: '0.75rem',
          color: '#000',
          borderColor: '#000',
          '&.Mui-selected': {
            backgroundColor: '#000',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#333',
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }
        }
      }}
    />
  );
}

// Page size constant
const PAGE_SIZE = 100;

// Modified indicator for changes
const ModifiedDot = styled('span')({
  display: 'inline-block',
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#52c41a',
  marginRight: '6px'
});

const ArticleZoningManager = ({ onChangeView }) => {
  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [etageFilter, setEtageFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [selectedEntrepot, setSelectedEntrepot] = useState('2'); // Default to '2'

  // Fetch entrepots
  const { entrepots } = useFetchAllEntrepots();

  // State for modifications
  const [modifications, setModifications] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Material-UI DataGrid refs and state
  const apiRef = useGridApiRef();
  const gridRef = useRef(null);
  const [focusedRowId, setFocusedRowId] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: PAGE_SIZE,
    page: 0,
  });
  const [tempInputValues, setTempInputValues] = useState({});
  
  // State for boundary navigation confirmation
  const [boundaryState, setBoundaryState] = useState({
    atFirstRow: false,
    atLastRow: false,
    timestamp: 0
  });

  // Fetch articles hook with background loading - pass selected entrepot
  const { 
    articles, 
    allArticles,
    total, 
    totalLoaded,
    initialLoading, 
    backgroundLoading,
    error, 
    applyFilters,
    refetch 
  } = useFetchArticlesZoneEtage(selectedEntrepot);

  // Bulk update hook
  const { 
    handleBulkUpdateZoneEtage, 
    loading: updateLoading, 
    error: updateError, 
    updateResult 
  } = useBulkUpdateZoneEtage();

  // Process data for DataGrid
  const processedRows = useMemo(() => {
    if (!Array.isArray(articles)) return [];
    return articles.map(article => ({
      ...article,
      id: article.code_art, // DataGrid requires 'id' field
      Zone: modifications[article.code_art]?.Zone ?? article.Zone ?? '',
      Etage: modifications[article.code_art]?.Etage ?? article.Etage ?? '',
      qte_stk: article.qte_stk || 0, // Include stock data
      isModified: !!modifications[article.code_art]
    }));
  }, [articles, modifications]);

  // Handle search/filter - now works on local data
  const handleSearch = useCallback(() => {
    applyFilters({ searchTerm, zoneFilter, etageFilter, stockFilter });
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [searchTerm, zoneFilter, etageFilter, stockFilter, applyFilters]);

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setZoneFilter('');
    setEtageFilter('');
    setStockFilter('');
    applyFilters({ searchTerm: '', zoneFilter: '', etageFilter: '', stockFilter: '' });
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [applyFilters]);

  // Handle entrepot change - clear modifications and refetch
  const handleEntrepotChange = useCallback((value) => {
    setSelectedEntrepot(value);
    setModifications({});
    setHasUnsavedChanges(false);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);

  // Auto-apply filters when values change (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      applyFilters({ searchTerm, zoneFilter, etageFilter, stockFilter });
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, zoneFilter, etageFilter, stockFilter, applyFilters]);

  // Handle field changes through DataGrid
  const processRowUpdate = useCallback((newRow, oldRow) => {
    try {
      const codeArt = newRow.code_art || newRow.id;
      
      // Check if Zone or Etage changed
      const zoneChanged = newRow.Zone !== oldRow.Zone;
      const etageChanged = newRow.Etage !== oldRow.Etage;
      
      if (zoneChanged || etageChanged) {
        const updatedModifications = {
          ...modifications,
          [codeArt]: {
            code_art: codeArt,
            Zone: newRow.Zone || null,
            Etage: newRow.Etage ? parseInt(newRow.Etage) || null : null,
          }
        };
        
        setModifications(updatedModifications);
        setHasUnsavedChanges(true);
      }

      return {
        ...newRow,
        isModified: true
      };
    } catch (error) {
      console.error('Error in processRowUpdate:', error);
      return oldRow;
    }
  }, [modifications]);

  // Handle save all modifications
  const handleSaveAll = useCallback(async () => {
    const articlesToUpdate = Object.values(modifications);
    
    if (articlesToUpdate.length === 0) {
      message.warning('No modifications to save');
      return;
    }

    try {
      await handleBulkUpdateZoneEtage(articlesToUpdate, selectedEntrepot);
      message.success(`Successfully updated ${articlesToUpdate.length} articles`);
      setModifications({});
      setHasUnsavedChanges(false);
      refetch(); // Refresh the data
    } catch (error) {
      message.error('Error saving modifications');
      console.error('Error saving modifications:', error);
    }
  }, [modifications, handleBulkUpdateZoneEtage, refetch, selectedEntrepot]);

  // Handle discard changes
  const handleDiscardChanges = useCallback(() => {
    setModifications({});
    setHasUnsavedChanges(false);
    message.info('Changes discarded');
  }, []);

  // Auto-clear boundary state after timeout
  useEffect(() => {
    if (boundaryState.atFirstRow || boundaryState.atLastRow) {
      const timeoutId = setTimeout(() => {
        setBoundaryState({ atFirstRow: false, atLastRow: false, timestamp: 0 });
      }, 2000); // Clear after 2 seconds

      return () => clearTimeout(timeoutId);
    }
  }, [boundaryState]);

  // Enhanced keyboard navigation with page switching
  useEffect(() => {
    if (!gridRef.current) return;
    
    const handleKeyDown = (e) => {
      // Handle arrow key navigation with page switching
      if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
        const focusedElement = document.querySelector('.MuiDataGrid-cell:focus');
        if (focusedElement) {
          const rowElement = focusedElement.closest('.MuiDataGrid-row');
          if (rowElement && rowElement.dataset.id) {
            const currentRowIndex = processedRows.findIndex(row => row.code_art === rowElement.dataset.id);
            const currentPage = paginationModel.page;
            const pageSize = paginationModel.pageSize;
            const totalPages = Math.ceil(processedRows.length / pageSize);
            
            // Calculate current position within the page
            const startIndex = currentPage * pageSize;
            const endIndex = Math.min(startIndex + pageSize - 1, processedRows.length - 1);
            const positionInPage = currentRowIndex - startIndex;
            const rowsInCurrentPage = endIndex - startIndex + 1;
            
                         const currentTime = Date.now();
             
             // Check if we're at the last row of current page and pressing down
             if (e.key === 'ArrowDown' && positionInPage === rowsInCurrentPage - 1) {
               // If there are more pages, handle confirmation
               if (currentPage < totalPages - 1) {
                 // Check if this is the second press within 2 seconds
                 if (boundaryState.atLastRow && (currentTime - boundaryState.timestamp) < 2000) {
                   // Second press - navigate to next page
                   e.preventDefault();
                   const nextPage = currentPage + 1;
                   setPaginationModel(prev => ({ ...prev, page: nextPage }));
                   setBoundaryState({ atFirstRow: false, atLastRow: false, timestamp: 0 });
                   
                   // Focus on the first row of the next page after a short delay
                   setTimeout(() => {
                     const firstRowInNextPage = processedRows[nextPage * pageSize];
                     if (firstRowInNextPage && apiRef.current) {
                       apiRef.current.setCellFocus(firstRowInNextPage.code_art, 'Zone');
                       setFocusedRowId(firstRowInNextPage.code_art);
                     }
                   }, 150);
                   return;
                 } else {
                   // First press - show confirmation and prevent default
                   e.preventDefault();
                   setBoundaryState({ atFirstRow: false, atLastRow: true, timestamp: currentTime });
                   return;
                 }
               }
             }
             
             // Check if we're at the first row of current page and pressing up
             if (e.key === 'ArrowUp' && positionInPage === 0) {
               // If there are previous pages, handle confirmation
               if (currentPage > 0) {
                 // Check if this is the second press within 2 seconds
                 if (boundaryState.atFirstRow && (currentTime - boundaryState.timestamp) < 2000) {
                   // Second press - navigate to previous page
                   e.preventDefault();
                   const prevPage = currentPage - 1;
                   setPaginationModel(prev => ({ ...prev, page: prevPage }));
                   setBoundaryState({ atFirstRow: false, atLastRow: false, timestamp: 0 });
                   
                   // Focus on the second-to-last row of the previous page after a short delay
                   setTimeout(() => {
                     const prevPageStartIndex = prevPage * pageSize;
                     const prevPageEndIndex = Math.min(prevPageStartIndex + pageSize - 1, processedRows.length - 1);
                     
                     // Go to second-to-last row (or last row if page has only 1 row)
                     const targetRowIndex = prevPageEndIndex > prevPageStartIndex ? prevPageEndIndex - 1 : prevPageEndIndex;
                     const targetRow = processedRows[targetRowIndex];
                     
                     if (targetRow && apiRef.current) {
                       apiRef.current.setCellFocus(targetRow.code_art, 'Zone');
                       setFocusedRowId(targetRow.code_art);
                     }
                   }, 150);
                   return;
                 } else {
                   // First press - show confirmation and prevent default
                   e.preventDefault();
                   setBoundaryState({ atFirstRow: true, atLastRow: false, timestamp: currentTime });
                   return;
                 }
               }
             }
             
             // Reset boundary state if we're not at boundaries anymore
             if ((e.key === 'ArrowUp' && positionInPage !== 0) || 
                 (e.key === 'ArrowDown' && positionInPage !== rowsInCurrentPage - 1)) {
               setBoundaryState({ atFirstRow: false, atLastRow: false, timestamp: 0 });
             }
          }
        }
      }
      
      // Regular focus tracking for all arrow keys
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
        setTimeout(() => {
          const focusedElement = document.querySelector('.MuiDataGrid-cell:focus');
          if (focusedElement) {
            const rowElement = focusedElement.closest('.MuiDataGrid-row');
            if (rowElement && rowElement.dataset.id) {
              setFocusedRowId(rowElement.dataset.id);
            }
          }
        }, 0);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
     }, [processedRows, paginationModel, apiRef, boundaryState]);

  // Column definitions
  const columns = [
    {
      field: 'code_art',
      headerName: 'Code Article',
      flex: 1.5,
      minWidth: 200,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            {params.row.isModified && <ModifiedDot />}
            <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '14px' }}>
              {params.value || ''}
            </span>
          </Box>
        );
      }
    },
    {
      field: 'Zone',
      headerName: 'Zone',
      flex: 1,
      minWidth: 150,
      editable: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        return (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            width: '100%',
            fontSize: '14px'
          }}>
            <span style={{ 
              color: params.row.isModified ? '#52c41a' : 'inherit',
              fontWeight: params.row.isModified ? 600 : 'normal'
            }}>
              {params.value || '-'}
            </span>
          </div>
        );
      }
    },
    {
      field: 'Etage',
      headerName: 'Etage',
      flex: 1,
      minWidth: 120,
      editable: true,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser: (value) => {
        const parsedValue = parseInt(value);
        return isNaN(parsedValue) ? null : parsedValue;
      },
      renderCell: (params) => {
        return (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            width: '100%',
            fontSize: '14px'
          }}>
            <span style={{ 
              color: params.row.isModified ? '#52c41a' : 'inherit',
              fontWeight: params.row.isModified ? 600 : 'normal'
            }}>
              {params.value || '-'}
            </span>
          </div>
        );
      }
    },
    {
      field: 'qte_stk',
      headerName: 'Stock',
      flex: 1,
      minWidth: 120,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        const stock = params.value || 0;
        let color = 'default';
        
        // Color coding based on stock levels
        if (stock <= 0) {
          color = 'error'; // Red for out of stock
        } else if (stock <= 10) {
          color = 'warning'; // Orange for low stock
        } else {
          color = 'success'; // Green for good stock
        }
        
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: '8px', 
            width: '100%' 
          }}>
            <Tag color={color} style={{ fontSize: '12px', margin: 0, fontWeight: '600' }}>
              {stock}
            </Tag>
            {params.row.isModified && (
              <span style={{ color: '#52c41a', fontSize: '12px', fontWeight: 'bold' }}>●</span>
            )}
          </div>
        );
      }
    }
  ];

  // Show error messages
  useEffect(() => {
    if (error) {
      message.error(`Error loading articles: ${error}`);
    }
  }, [error]);

  useEffect(() => {
    if (updateError) {
      message.error(`Error saving changes: ${updateError}`);
    }
  }, [updateError]);

  useEffect(() => {
    if (updateResult) {
      if (updateResult.errors?.length > 0) {
        message.warning(`Updated ${updateResult.updated} articles with ${updateResult.errors.length} errors`);
      }
    }
  }, [updateResult]);

  // Show notification when background loading completes
  useEffect(() => {
    if (!backgroundLoading && totalLoaded > 100 && totalLoaded === total) {
      message.success(`All ${total} articles loaded successfully!`);
    }
  }, [backgroundLoading, totalLoaded, total]);

  if (initialLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Loading articles...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {onChangeView && (
            <Button 
              icon={<ArrowLeftOutlined />}
              onClick={() => onChangeView('list')}
              type="default"
            >
              Back to Articles
            </Button>
          )}
          <div>
            <Title level={2} style={{ margin: 0 }}>Article Zoning Management</Title>
            <Space style={{ marginTop: '8px' }}>
              <Text type="secondary">
                Showing: {articles.length} / Loaded: {totalLoaded} / Total: {total}
              </Text>
              <Text type="secondary" style={{ marginLeft: '16px' }}>
                Entrepôt:
              </Text>
              <Select
                value={selectedEntrepot}
                onChange={handleEntrepotChange}
                style={{ width: 200 }}
                size="small"
              >
                {entrepots && entrepots.map(entrepot => (
                  <Select.Option key={entrepot.code_ent} value={entrepot.code_ent}>
                    {entrepot.code_ent} - {entrepot.name}
                  </Select.Option>
                ))}
              </Select>
              <Tag color="blue" style={{ fontSize: '11px' }}>
                 Page {paginationModel.page + 1} of {Math.ceil(articles.length / paginationModel.pageSize)}
               </Tag>
               {boundaryState.atFirstRow && (
                 <Tag color="orange" style={{ fontSize: '11px', animation: 'confirmationPulse 1s infinite' }}>
                   ⬆️ Press ↑ again to go to previous page
                 </Tag>
               )}
               {boundaryState.atLastRow && (
                 <Tag color="orange" style={{ fontSize: '11px', animation: 'confirmationPulse 1s infinite' }}>
                   ⬇️ Press ↓ again to go to next page
                 </Tag>
               )}
               {backgroundLoading && (
                 <Tag color="processing">
                   Loading more articles...
                 </Tag>
               )}
               {hasUnsavedChanges && (
                 <Tag color="warning">
                   Unsaved Changes: {Object.keys(modifications).length}
                 </Tag>
               )}
            </Space>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <MuiCard style={{ marginBottom: '16px' }}>
        <CardContent>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <Input
              placeholder="Search by article name or code... (real-time)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            />
            <Input
              placeholder="Filter by Zone"
              value={zoneFilter}
              onChange={(e) => setZoneFilter(e.target.value)}
              allowClear
            />
            <Input
              type="number"
              placeholder="Filter by Etage"
              value={etageFilter}
              onChange={(e) => setEtageFilter(e.target.value)}
              allowClear
            />
            <Input
              placeholder="Stock filter (e.g. >10, <5, =0)"
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              allowClear
            />
          </div>
          <Space>
            <Button 
              icon={<ClearOutlined />} 
              onClick={handleClearSearch}
              disabled={!searchTerm && !zoneFilter && !etageFilter && !stockFilter}
            >
              Clear All Filters
            </Button>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Filters apply automatically as you type
              </Text>
              <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                Stock filter examples: {'>'}10 (more than 10), {'<'}5 (less than 5), =0 (exactly 0), {'>='}20, {'<='}5
              </Text>
                             <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic', color: '#1890ff' }}>
                 💡 Use ↑/↓ arrow keys to navigate. Page switching: Press ↓ twice at last row for next page, ↑ twice at first row for previous page (confirmation required).
               </Text>
            </div>
          </Space>
        </CardContent>
      </MuiCard>

      {/* Action Buttons */}
      <MuiCard style={{ marginBottom: '16px' }}>
        <CardContent>
          <Space>
            <PermissionButton permissionModule="articles" permissionAction="zoning"
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSaveAll} 
              disabled={!hasUnsavedChanges}
              loading={updateLoading}
            >
              Save All ({Object.keys(modifications).length})
            </PermissionButton>
            <PermissionButton
              icon={<UndoOutlined />}
              onClick={handleDiscardChanges} 
              disabled={!hasUnsavedChanges}
              danger
            >
              Discard Changes
            </PermissionButton>
          </Space>
        </CardContent>
      </MuiCard>

      {/* Articles DataGrid */}
      <Box sx={{ 
        width: '100%',
        '& .modified-row': {
          backgroundColor: 'rgba(82, 196, 26, 0.08)',
          borderLeft: '3px solid #52c41a',
        },
        '& .focused-row': {
          borderTop: '2px solid black',
          borderBottom: '2px solid black',
        }
      }}>
        <DataGrid
          ref={gridRef}
          apiRef={apiRef}
          rows={processedRows}
          columns={columns}
          getRowId={(row) => row.code_art}
          rowHeight={45}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(error) => console.error('Error updating row:', error)}
          disableColumnMenu
          
          // Focus tracking
          onCellClick={(params) => {
            setFocusedRowId(params.id);
          }}
          onRowClick={(params) => {
            setFocusedRowId(params.id);
          }}
          onCellFocusIn={(params) => {
            setFocusedRowId(params.id);
          }}
          
          // Enable pagination with custom component
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[PAGE_SIZE]}
          slots={{
            pagination: CustomPagination,
          }}
          
          autoHeight={false}
          sx={{
            height: 600,
            maxHeight: '60vh',
            width: '100%',
            overflowY: 'auto',
            [`& .${gridClasses.cell}`]: {
              fontSize: '14px',
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            [`& .${gridClasses.columnHeader}`]: {
              fontSize: '15px',
              fontWeight: '600',
              backgroundColor: '#fafafa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            [`& .${gridClasses.cell}:focus`]: {
              outline: 'none',
              border: 'solid #52c41a 2px',
              borderRadius: '4px',
            },
            [`& .${gridClasses.row}:hover`]: {
              backgroundColor: 'rgba(82, 196, 26, 0.04)',
              cursor: 'pointer',
            },
            [`& .${gridClasses.row}.Mui-selected`]: {
              backgroundColor: 'rgba(82, 196, 26, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(82, 196, 26, 0.12)',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              border: 'none',
              backgroundColor: '#fafafa',
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: '#ffffff',
            }
          }}
          
          initialState={{
            sorting: {
              sortModel: [{ field: 'code_art', sort: 'asc' }],
            },
          }}
          
          getRowClassName={(params) => {
            const classes = [];
            
            if (params.row.isModified) {
              classes.push('modified-row');
            }
            
            if (params.id === focusedRowId) {
              classes.push('focused-row');
            }
            
            return classes.join(' ');
          }}
          
          editMode="cell"
          disableMultipleColumnsSorting
          disableMultipleSelection
          disableSelectionOnClick={false}
          
          // Enhanced keyboard navigation handling
          onCellKeyDown={(params, event) => {
            // Let our custom navigation handler manage up/down arrows for page switching
            if (['ArrowUp', 'ArrowDown'].includes(event.key)) {
              // Our custom handler will manage page switching
              return;
            }
            
            // Allow normal DataGrid behavior for other navigation keys
            if (['ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'].includes(event.key)) {
              return;
            }
            
            // For typing, ensure we enter edit mode properly
            if (!event.ctrlKey && !event.metaKey && !event.altKey && event.key.length === 1) {
              // Allow the character to be typed in edit mode
              return;
            }
          }}
        />
      </Box>
    </div>
  );
};

export default ArticleZoningManager;